import { FC, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { SaveButton } from "components/buttons/SaveButton"
import { Toggle } from "components/inputs/Toggle"

import { settingsService } from "services/settings/settings.service"

import { GameCardConfig, GameCardMode } from "types/api"

interface GameCardSectionProps {
  config: GameCardConfig | null
  onChange: (config: GameCardConfig) => void
}

const isValid = (config: GameCardConfig | null): boolean => {
  if (!config) return false
  if (!config.enabled) return true
  if (!config.mode) return false
  const { min, max } = config.range ?? {}
  if (min === undefined || min === null || max === undefined || max === null) return false
  return min >= 0 && max >= 0 && min <= max
}

export const GameCardSection: FC<GameCardSectionProps> = ({ config, onChange }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    if (!config) return
    setSaving(true)
    try {
      const payload = config.enabled ? config : { enabled: false }
      const success = await settingsService.updateGameCardConfig(payload as GameCardConfig)
      if (success) toast.success(t("settings.general.gameCardConfig.updated") as string)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h4 className="text-base font-semibold text-[rgba(6,7,21,1)] dark:text-white">
          {t("settings.general.gameCardConfig.sectionTitle")}
        </h4>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t("settings.general.gameCardConfig.sectionDescription")}</p>
      </div>

      <div className="flex flex-col max-w-2xl gap-4 rounded-lg border border-black/10 dark:border-white/10 p-4 bg-white dark:bg-white/[0.04]">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium text-gray-800 dark:text-white">{t("settings.general.gameCardConfig.enabled.label")}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">{t("settings.general.gameCardConfig.enabled.description")}</span>
          </div>
          <Toggle
            checked={config?.enabled ?? false}
            onChange={() => onChange({ ...config, enabled: !config?.enabled })}
            disabled={saving}
          />
        </div>

        {config?.enabled && (
          <>
            <div className="border-t border-black/10 dark:border-white/10" />

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4">
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-sm font-medium text-gray-800 dark:text-white">{t("settings.general.gameCardConfig.mode.label")}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">{t("settings.general.gameCardConfig.mode.description")}</span>
              </div>
              <select
                value={config?.mode ?? ""}
                onChange={(e) => onChange({ ...config, mode: e.target.value as GameCardMode })}
                disabled={saving}
                className="text-sm rounded-md border border-black/20 dark:border-white/20 bg-white dark:bg-[#0C0C1A] text-gray-800 dark:text-white px-3 py-2 disabled:opacity-50 w-full sm:w-auto sm:shrink-0"
              >
                <option value="">{t("settings.general.gameCardConfig.mode.selectMode")}</option>
                <option value="dynamic">{t("settings.general.gameCardConfig.mode.dynamic")}</option>
                <option value="static">{t("settings.general.gameCardConfig.mode.static")}</option>
              </select>
            </div>

            <div className="border-t border-black/10 dark:border-white/10" />

            <div className="flex flex-col gap-3">
              <span className="text-sm font-medium text-gray-800 dark:text-white">{t("settings.general.gameCardConfig.range.label")}</span>
              <div className="flex gap-3">
                <div className="flex flex-col gap-1 flex-1">
                  <label className="text-xs text-gray-600 dark:text-gray-300">{t("settings.general.gameCardConfig.range.min")}</label>
                  <input
                    type="number"
                    min="0"
                    value={config?.range?.min ?? ""}
                    onChange={(e) =>
                      onChange({ ...config, range: { ...config?.range, min: e.target.value ? Number(e.target.value) : undefined } })
                    }
                    disabled={saving}
                    placeholder="0"
                    className="text-sm rounded-md border border-black/20 dark:border-white/20 bg-white dark:bg-[#0C0C1A] text-gray-800 dark:text-white px-3 py-2 disabled:opacity-50"
                  />
                </div>
                <div className="flex flex-col gap-1 flex-1">
                  <label className="text-xs text-gray-600 dark:text-gray-300">{t("settings.general.gameCardConfig.range.max")}</label>
                  <input
                    type="number"
                    min="0"
                    value={config?.range?.max ?? ""}
                    onChange={(e) =>
                      onChange({ ...config, range: { ...config?.range, max: e.target.value ? Number(e.target.value) : undefined } })
                    }
                    disabled={saving}
                    placeholder="100"
                    className="text-sm rounded-md border border-black/20 dark:border-white/20 bg-white dark:bg-[#0C0C1A] text-gray-800 dark:text-white px-3 py-2 disabled:opacity-50"
                  />
                </div>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">{t("settings.general.gameCardConfig.range.description")}</span>
            </div>
          </>
        )}
      </div>

      <div className="flex gap-2">
        <SaveButton onClick={handleSave} loading={saving} disabled={saving || !isValid(config)}>
          {t("common.save")}
        </SaveButton>
      </div>
    </div>
  )
}
